import React from 'react';
import { Link } from 'react-router-dom';
import { Formik, Form, Field as FormikField } from 'formik';
import * as Yup from 'yup';

import { AuthShell } from '../../design-system/components/shells';
import {
  AuthBanner,
  AuthConsent,
  AuthField,
  AuthFields,
  AuthFootnote,
  AuthHint,
  AuthInput,
  AuthPasswordInput,
  AuthStrength,
  AuthSubmit,
} from '../../design-system/patterns/auth';
import { routes } from '../../routes/routes';

/**
 * Sign up, the view. Pure: every input is a prop, every output is a callback.
 *
 * The container (`Signup.jsx`) owns `useAuth`, `authModule.register`, the
 * analytics and every redirect. This file owns what the user sees, so the state
 * explorer can mount the real screen with fixture props.
 *
 * DELIBERATE DIFFERENCES FROM THE UPSTREAM TEMPLATE
 * -------------------------------------------------
 * 1. NO "Continue with Google" and no "or" divider. The template leads with
 *    both, but this app has no Google identity provider: `grep -ri google src`
 *    finds a Places autocomplete and nothing else. Shipping the button would be
 *    shipping a control that cannot work.
 *
 * 2. The template's first field is a "Display name" with a "buyers see this"
 *    hint. The app's field is `full_name`, a real name, and what buyers see is
 *    the store name set during seller registration. The hint would be wrong, so
 *    it is not ported; the label says Full name because that is the field.
 *
 * 3. The template has no confirm-password field. The app validates one and the
 *    conversion does not change behaviour, so it stays.
 *
 * 4. The template's submit is rendered disabled until the form is valid. Formik
 *    owns validity here and the app has always allowed a submit attempt that
 *    surfaces the field errors, so the button stays live and disables only
 *    while a request is in flight.
 *
 * The Terms and Privacy links are the app's existing placeholders. There are no
 * legal routes in `routes.js` yet, so they stay as they were rather than being
 * pointed at pages that do not exist.
 */

export const signupValidationSchema = Yup.object({
  full_name: Yup.string()
    .min(2, 'Name must be at least 2 characters')
    .max(50, 'Name must be less than 50 characters')
    .required('Full name is required'),
  email: Yup.string().email('Invalid email address').required('Email is required'),
  password: Yup.string()
    .min(8, 'Password must be at least 8 characters')
    .matches(
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
      'Password must contain at least one uppercase letter, one lowercase letter, and one number'
    )
    .required('Password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords must match')
    .required('Please confirm your password'),
  role: Yup.string()
    .oneOf(['buyer', 'seller'], 'Please select a valid role')
    .required('Role is required'),
  agreeToTerms: Yup.boolean()
    .oneOf([true], 'You must agree to the Terms of Service and Privacy Policy')
    .required('You must agree to the Terms of Service and Privacy Policy'),
});

export const SIGNUP_INITIAL_VALUES = {
  full_name: '',
  email: '',
  password: '',
  confirmPassword: '',
  role: 'buyer',
  agreeToTerms: false,
};

export function SignupView({
  onSubmit,
  loading = false,
  error,
  message,
  initialValues = SIGNUP_INITIAL_VALUES,
  initialErrors,
  initialTouched,
}) {
  return (
    <AuthShell
      title="Create your account"
      subtitle="Free to join. Start a watchlist and track what your collection is worth."
      back={{ to: routes.MARKETPLACE, label: 'Back to marketplace' }}
      footer={
        <AuthFootnote>
          Already have an account? <Link to={routes.LOGIN}>Sign in</Link>
        </AuthFootnote>
      }
    >
      <AuthBanner tone="success">{message}</AuthBanner>
      <AuthBanner tone="error">{error}</AuthBanner>

      <Formik
        initialValues={{ ...SIGNUP_INITIAL_VALUES, ...initialValues }}
        initialErrors={initialErrors}
        initialTouched={initialTouched}
        validationSchema={signupValidationSchema}
        onSubmit={onSubmit}
      >
        {({ isSubmitting, errors, touched, values, handleChange, handleBlur }) => {
          const busy = isSubmitting || loading;
          const shown = (name) => (touched[name] && errors[name] ? errors[name] : undefined);

          return (
            <Form noValidate>
              <AuthFields>
                <AuthField label="Full name" error={shown('full_name')}>
                  <FormikField
                    as={AuthInput}
                    name="full_name"
                    type="text"
                    placeholder="Thabo Mokoena"
                    autoComplete="name"
                    disabled={busy}
                  />
                </AuthField>

                <AuthField label="Email address" error={shown('email')}>
                  <FormikField
                    as={AuthInput}
                    name="email"
                    type="email"
                    placeholder="you@example.co.za"
                    autoComplete="email"
                    disabled={busy}
                  />
                </AuthField>

                <AuthField
                  label="Password"
                  error={shown('password')}
                  hint="At least 8 characters, with an uppercase letter, a lowercase letter and a number."
                >
                  <FormikField
                    as={AuthPasswordInput}
                    name="password"
                    placeholder="Enter your password"
                    autoComplete="new-password"
                    disabled={busy}
                  />
                </AuthField>

                <AuthStrength value={values.password} />

                <AuthField label="Confirm password" error={shown('confirmPassword')}>
                  <FormikField
                    as={AuthPasswordInput}
                    name="confirmPassword"
                    placeholder="Confirm your password"
                    autoComplete="new-password"
                    disabled={busy}
                  />
                </AuthField>
              </AuthFields>

              <AuthConsent
                name="agreeToTerms"
                checked={values.agreeToTerms}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={busy}
                error={shown('agreeToTerms')}
              >
                I agree to the {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a href="#">Terms of Service</a> and{' '}
                {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
                <a href="#">Privacy Policy</a>.
              </AuthConsent>

              <AuthSubmit loading={busy} loadingLabel="Creating account">
                Create account
              </AuthSubmit>

              <AuthHint>You can set up selling later.</AuthHint>
            </Form>
          );
        }}
      </Formik>
    </AuthShell>
  );
}

export default SignupView;
